import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, AcademicCapIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { fetchCourses, createCourse, updateCourse, deleteCourse } from '../../store/slices/courseSlice';
import { fetchCategories } from '../../store/slices/categorySlice';
import { fetchUniversities } from '../../store/slices/universitySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, SearchInput, Pagination, ConfirmDialog, Card, Modal, Input, Select, FileUpload, DynamicBadge } from '../../components/Common';
import { useOptions } from '../../hooks/useOptions';

const CourseList = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { courses, loading, pagination } = useSelector((state) => state.course);
  const { categories } = useSelector((state) => state.category);
  const { universities } = useSelector((state) => state.university);
  
  // Use dynamic options
  const { getSelectOptions: getLevelOptions } = useOptions('courseLevel');
  const { getSelectOptions: getStatusOptions } = useOptions('courseStatus');
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    title: '', description: '', instructor: '', duration: '',
    level: 'beginner', price: 0, category_id: '', university_id: '', status: 'draft'
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchCourses({ page, size: pageSize, search }));
    dispatch(fetchCategories({ size: 100 }));
    dispatch(fetchUniversities({ size: 100 }));
  }, [dispatch, page, pageSize, search]);

  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      openModal();
    }
  }, [searchParams]);

  const openModal = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        title: course.title || '', description: course.description || '',
        instructor: course.instructor || '', duration: course.duration || '',
        level: course.level || 'beginner', price: course.price || 0,
        category_id: course.category_id?.toString() || '', university_id: course.university_id?.toString() || '',
        status: course.status || 'draft'
      });
    } else {
      setEditingCourse(null);
      setFormData({
        title: '', description: '', instructor: '', duration: '',
        level: 'beginner', price: 0, category_id: '', university_id: '', status: 'draft'
      });
    }
    setThumbnail(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      dispatch(showError('Course title is required'));
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      if (thumbnail) submitData.append('image', thumbnail);

      if (editingCourse) {
        await dispatch(updateCourse({ id: editingCourse.id, data: submitData })).unwrap();
        dispatch(showSuccess('Course updated successfully'));
      } else {
        await dispatch(createCourse(submitData)).unwrap();
        dispatch(showSuccess('Course created successfully'));
      }
      setModalOpen(false);
      dispatch(fetchCourses({ page, size: pageSize, search }));
    } catch (error) {
      console.error('Submit error:', error);
      // Display the specific error message
      const errorMessage = typeof error === 'string' ? error : 
                          error?.message || 
                          error?.error || 
                          `Failed to ${editingCourse ? 'update' : 'create'} course`;
      dispatch(showError(errorMessage));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteCourse(deleteModal.id)).unwrap();
        dispatch(showSuccess('Course deleted successfully'));
        dispatch(fetchCourses({ page, size: pageSize, search }));
      } catch (error) {
        console.error('Delete error:', error);
        // Display the specific error message
        const errorMessage = typeof error === 'string' ? error : 
                            error?.message || 
                            error?.error || 
                            'Failed to delete course';
        dispatch(showError(errorMessage));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  // Get dynamic options
  const levelOptions = getLevelOptions();
  const statusOptions = getStatusOptions();

  const categoryOptions = categories.map(cat => ({ value: cat.id.toString(), label: cat.name }));
  const universityOptions = universities.map(uni => ({ value: uni.id.toString(), label: uni.name }));

  // Helper to get university name by ID
  const getUniversityName = (universityId) => {
    if (!universityId) return null;
    const uni = universities.find(u => u.id === parseInt(universityId));
    return uni?.name || null;
  };

  const columns = [
    {
      key: 'title',
      title: 'Course',
      render: (value, row) => (
        <div>
          <p className="font-medium text-slate-800 line-clamp-1">{value || 'Untitled'}</p>
          <p className="text-sm text-slate-500">{row.instructor || 'No instructor'}</p>
        </div>
      ),
    },
    {
      key: 'university_id',
      title: 'University',
      render: (value, row) => {
        const uniName = row.university?.name || getUniversityName(value);
        return uniName ? (
          <div className="flex items-center gap-2">
            <BuildingLibraryIcon className="w-4 h-4 text-emerald-600" />
            <span className="text-slate-700 text-sm">{uniName}</span>
          </div>
        ) : <span className="text-slate-400 text-sm">-</span>;
      },
    },
    {
      key: 'level',
      title: 'Level',
      render: (value) => <DynamicBadge optionType="courseLevel" value={value} />,
    },
    {
      key: 'price',
      title: 'Price',
      render: (value, row) => (
        <span className="font-medium text-slate-800">
          {value > 0 ? `${row.currency || '$'}${value}` : 'Free'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => <DynamicBadge optionType="courseStatus" value={value} dot />,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Link to={`/courses/edit/${row.id}`} className="p-2 hover:bg-slate-100 rounded-lg inline-block" title="Edit">
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </Link>
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Courses</h1>
          <p className="text-slate-500">Manage educational courses</p>
        </div>
        <Link to="/courses/new"><Button icon={PlusIcon}>New Course</Button></Link>
      </div>

      <Card noPadding>
        <div className="p-4 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search courses..." className="max-w-md" />
        </div>

        <Table columns={columns} data={courses} loading={loading} emptyMessage="No courses found" emptyIcon={AcademicCapIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* Course Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingCourse ? 'Edit Course' : 'New Course'} size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting}>{editingCourse ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Title *" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Course title" required />
          <Input label="Description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Short description" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Instructor" value={formData.instructor} onChange={(e) => setFormData(prev => ({ ...prev, instructor: e.target.value }))} placeholder="Instructor name" />
            <Input label="Duration" value={formData.duration} onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))} placeholder="e.g., 10 hours" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Level" value={formData.level} onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))} options={levelOptions} />
            <Input label="Price" type="number" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} placeholder="0" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Category" value={formData.category_id} onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))} options={categoryOptions} placeholder="Select category" />
            <Select label="University" value={formData.university_id} onChange={(e) => setFormData(prev => ({ ...prev, university_id: e.target.value }))} options={universityOptions} placeholder="Select university" />
          </div>
          <Select label="Status" value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))} options={statusOptions} />
          <FileUpload label="Thumbnail" accept="image/*" maxSize={5} onChange={setThumbnail} preview={editingCourse?.thumbnail_url} />
        </form>
      </Modal>

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Course" message="Are you sure you want to delete this course?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default CourseList;
